import {eventApi} from '~/CreateFabricInvitation/api/endpoints/event';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {history} from '~/CreateFabricInvitation/history';
import {setInEditMode} from '~/CreateFabricInvitation/navigation/utils';
import {
  checkPhotoUploadStatus,
  validateEventDetails,
} from '~/CreateFabricInvitation/navigation/validation';
import {selectIsDirty} from '~/CreateFabricInvitation/reducers/details/selectors';
import {canvasRemoveTextBordersAndIcons} from '~/common/canvas/customs/text_border';
import {HTTP_STATUS_PAYMENT_REQUIRED} from '~/common/fabric/constants';
import {
  eventStepsType,
  isInFreeAddGuestsStepExpVariant,
} from '~/common/fabric/EventGuests/components/DisplayCondition/addGuestsCondition';
import {
  PLAN_NAME_PARTY_PASS,
  PLAN_NAME_PREMIUM_PASS,
  PLAN_NAME_PREMIUM_PASS_SUBSCRIPTION,
  PLAN_NAME_PRO,
} from '~/common/fabric/routes/EventReview/constants';
import {isSubscriptionPlan} from '~/common/fabric/User/utils';
import {isFabricWebview} from '~/common/utils/webview';

export const sendEvent = async (store) => {
  const state = store.getState();
  return store.dispatch(eventApi.endpoints.send.initiate(state.eviteEvent.id));
};

const sendAndHandleError = async (store) => {
  const state = store.getState();
  const sendResponse = await sendEvent(store);
  if (sendResponse?.error?.status === HTTP_STATUS_PAYMENT_REQUIRED) {
    history.replace({
      pathname: `/${state.eviteEvent.id}/review`,
      hash: history.location.hash,
      search: history.location.search,
    });
    return false;
  }
  if (sendResponse?.error) {
    if (sendResponse.error.data?.message) {
      // when you cancel the captcha, there will be no data from the query,
      // so only show a warning to the user if we have an actual uncaught error
      evite.error(`Error sending event ${state.eviteEvent.id}`, sendResponse.error);
      throw new Error(sendResponse.error.data?.message);
    }
    return false;
  }
  return true;
};

const checkSaveAndSendBeforeLeave = async (store, route, forceCheck = false) => {
  if (route === 'next' || forceCheck) {
    const state = store.getState();
    const hasSub = isSubscriptionPlan(state?.user?.subscriptionPlanName, false);
    if (hasSub || window.eventHasProHost || state.eviteEvent.previously_purchased_offer) {
      await store.dispatch(saveEventThunk());
      const sentSuccessfully = await sendAndHandleError(store);
      if (!sentSuccessfully) return sentSuccessfully;
      history.replace({
        pathname: `/${state.eviteEvent.id}/confirm`,
        hash: history.location.hash,
        search: history.location.search,
      });
      return false;
    }
  }
  return true;
};

const preCheckoutButtonText = (store) => {
  const planName = store.getState().user.subscriptionPlanName;
  const hasPartyPass = planName === PLAN_NAME_PARTY_PASS;
  const hasPro = planName === PLAN_NAME_PRO;
  const hasPremiumPass =
    planName === PLAN_NAME_PREMIUM_PASS || planName === PLAN_NAME_PREMIUM_PASS_SUBSCRIPTION;
  let text = 'Check out';
  if (hasPremiumPass || hasPartyPass || window.eventHasProHost || hasPro) {
    text = 'Finish & send';
  }
  return text;
};

export const createStepsAB = {
  meta: {
    // Not a step - global settings for steps
    editing: false,
    creating: true,
    showBackButton: true,
    countSteps: true,
  },
  customize: {
    id: 1,
    name: 'customize',
    path: 'customize',
    displayName: 'Design',
    title: 'Customize Your Invitation',
    nextRoute: 'details',
    previousRoute: null,
    saveOnLeave: true,
    nextButtonText: 'Next',
    hideInWebView: false,
    hideBottomNav: true,
    preLeaveRouteFunction: (store, routeName, canvas) => canvasRemoveTextBordersAndIcons(canvas),
  },
  details: {
    id: 2,
    name: 'details',
    path: 'details',
    displayName: 'Details',
    title: 'Enter Or Edit Your Event Details',
    nextRouteAvailableSelector: (state) => !selectIsDirty(state),
    preLeaveRouteFunction: (store, router) => {
      const validEventDetails = validateEventDetails(store);
      const safeToNavigate = checkPhotoUploadStatus(store, router === 'previous');
      return validEventDetails && safeToNavigate;
    },
    nextRoute: 'gifting',
    previousRoute: 'customize',
    saveOnLeave: true,
    nextButtonText: 'Next',
    hideInWebView: false,
    hideBottomNav: true,
  },
  gifting: {
    id: 3,
    path: 'gifting',
    name: 'gifting',
    displayName: 'Gifting',
    title: "Select one or more gift options you'd like to include in your invitation",
    preLeaveRouteFunction: (store, router) => {
      const safeToNavigate = checkPhotoUploadStatus(store, router !== 'previous');
      return safeToNavigate;
    },
    nextRoute: 'preview',
    previousRoute: 'details',
    saveOnLeave: true,
    nextButtonText: 'Next',
    hideInWebView: false,
    hideBottomNav: true,
  },
  preview: {
    id: 4,
    path: 'preview',
    name: 'preview',
    displayName: 'Review',
    title: `This Is A Review Of Your Invitation`,
    nextRoute: 'guests',
    previousRoute: 'gifting',
    saveOnLeave: true,
    nextButtonText: (store) => {
      const {details = {}} = store.getState();
      // TODO: Text changes: "Check out" if Freemium/host needs more money to send to currently-added guests
      return details.isPremium ? 'Next' : 'Finish';
    },
    hideInWebView: false,
    hideBottomNav: true,
    previewButtons: !isFabricWebview(),
  },
  guests: {
    id: 5,
    path: 'guests',
    name: 'guests',
    displayName: 'Add guests',
    title: 'Add guests',
    nextRoute: 'review',
    preLeaveRouteFunction: checkSaveAndSendBeforeLeave,
    previousRoute: 'preview',
    saveOnLeave: false,
    nextButtonText: (store) => {
      const state = store.getState();
      const hasSub = isSubscriptionPlan(state.user?.subscriptionPlanName, false);
      const isFreeEvent = !state.details.isPremium;
      return hasSub || window.eventHasProHost || isFreeEvent ? 'Finish & send' : 'Check out';
    },
    hideInWebView: true,
    hideBottomNav: true,
  },
  review: {
    id: 6,
    path: 'review',
    name: 'review',
    displayName: 'Checkout',
    title: 'Checkout',
    nextRoute: (store) => {
      const {details = {}} = store.getState();
      // if premium design or user has downgraded
      if (details.isPremium || details.hideUpsell || details.downgraded) {
        return 'confirm';
      }
      return 'send';
    },
    previousRoute: 'guests',
    saveOnLeave: false,
    nextButtonText: 'Finish & send', // TODO: Text changes: "Check out" if host needs more money to send to currently-added guests
    hideInWebView: true,
    hideBottomNav: true,
    isUpsellMode: (store) => !!store.getState().details.isUpsellMode,
    previewButtons: false,
  },
  send: {
    id: 7,
    path: 'send',
    name: 'send',
    displayName: 'Upgrade to Premium',
    title: 'Upgrade to Premium',
    preLeaveRouteFunction: async (store, route) => {
      // AB TEST: 'test_add-guests-step-free' - TO ROLL BACK: Remove this conditional check
      if (isInFreeAddGuestsStepExpVariant(store.getState())) return true; // Proceed without sending Invitation
      if (route !== 'exit') {
        const sentSuccessfully = await sendAndHandleError(store);
        if (!sentSuccessfully) return sentSuccessfully;
        await setInEditMode(store);
      }
      return true;
    },
    previousRoute: 'review',
    nextRoute: 'confirm',
    nextButtonText: 'Upgrade to Premium',
    hideInWebView: true,
    hideBottomNav: true,
    showSimpleNav: true,
    showFooter: false,
  },
  confirm: {
    id: 8,
    path: 'confirm',
    name: 'confirm',
    displayName: 'Confirmation',
    title: 'Confirmation',
    // nextRoute: 'confirm',
    previousRoute: 'review',
    saveOnLeave: false,
    // nextButtonText: 'Purchase & Send', // TODO: text changes for free/premium??
    hideInWebView: true,
    hideBottomNav: true,
    showMegaNav: true,
    showFooter: true,
  },
  exit: {
    // Routes us to the source page
    id: -1,
    path: !document.referrer?.includes?.('login') && document.referrer ? document.referrer : '/',
    hideInWebView: true,
    hideBottomNav: true,
  },
};

export const freemiumSteps = {
  ...createStepsAB,
  preview: {
    ...createStepsAB.preview,
    nextRoute: createStepsAB.guests.nextRoute,
    preLeaveRouteFunction: checkSaveAndSendBeforeLeave,
    nextButtonText: preCheckoutButtonText,
  },
  review: {
    ...createStepsAB.review,
    id: createStepsAB.guests.id,
    displayName: 'Checkout',
    previousRoute: createStepsAB.guests.previousRoute,
    nextButtonText: 'Check out',
  },
};

export const freeSteps = {
  ...createStepsAB,
  review: {
    ...createStepsAB.review,
    id: 5,
  },
  preview: {
    ...createStepsAB.preview,
    name: 'preview',
    previousRoute: 'gifting',
    preLeaveRouteFunction: async (store, routeName) => {
      if (['send', 'confirm'].includes(routeName)) {
        const sendResponse = await sendEvent(store);
        return !sendResponse?.error;
      }
      return true;
    },
    nextRoute: createStepsAB.review.nextRoute,
    nextButtonText: 'Finish',
  },
};

export const freeWithGuestsSteps = {
  ...freeSteps,
  preview: {
    ...freeSteps.preview,
    nextRoute: 'guests',
    nextButtonText: 'Next',
    preLeaveRouteFunction: null,
  },
  guests: {
    ...freeSteps.guests,
    nextRoute: createStepsAB.review.nextRoute,
    nextButtonText: 'Finish & send',
    preLeaveRouteFunction: freeSteps.preview.preLeaveRouteFunction,
  },
  review: {
    ...freeSteps.review,
    id: 6,
  },
};

// AB TEST: 'test_add-guests-step-free' - TO ROLL BACK: Remove this entire step configuration
export const freeWithGuestsABSteps = {
  ...freeWithGuestsSteps,
  preview: {
    ...freeWithGuestsSteps.preview,
    nextRoute: (store) =>
      store.getState().details.isPremium || store.getState().details.downgraded ? 'guests' : 'send',
  },
  guests: {
    ...freeWithGuestsSteps.guests,
    nextRoute: 'confirm',
  },
  send: {
    ...freeWithGuestsSteps.send,
    nextRoute: 'preview',
    saveOnLeave: false,
  },
};

export const freemiumWithGuestsSteps = {
  ...freemiumSteps,
  preview: {
    ...freemiumSteps.preview,
    nextRoute: createStepsAB.preview.nextRoute,
    nextButtonText: createStepsAB.preview.nextButtonText,
    preLeaveRouteFunction: null,
  },
  guests: {
    ...createStepsAB.guests,
    preLeaveRouteFunction: checkSaveAndSendBeforeLeave,
    nextButtonText: preCheckoutButtonText,
  },
  review: {
    ...createStepsAB.review,
    displayName: 'Checkout',
    nextButtonText: 'Check out',
  },
};

export const premiumAddGuestsAfterSteps = {
  ...createStepsAB,
  preview: {
    ...createStepsAB.preview,
    nextRoute: createStepsAB.guests.nextRoute,
    preLeaveRouteFunction: checkSaveAndSendBeforeLeave,
    nextButtonText: preCheckoutButtonText,
  },
  review: {
    ...createStepsAB.review,
    id: createStepsAB.guests.id,
    displayName: 'Checkout',
    previousRoute: createStepsAB.guests.previousRoute,
    nextButtonText: 'Check out',
  },
};

export const eventStepsAB = {
  [eventStepsType.PREMIUM]: createStepsAB,
  [eventStepsType.PREMIUM_ADD_GUESTS_AFTER]: premiumAddGuestsAfterSteps,
  [eventStepsType.FREEMIUM_WITH_GUESTS]: freemiumWithGuestsSteps,
  [eventStepsType.FREEMIUM]: freemiumSteps,
  [eventStepsType.FREE_WITH_GUESTS]: freeWithGuestsSteps,
  // AB TEST: 'test_add-guests-step-free' - TO ROLL BACK: Remove this line
  [eventStepsType.FREE_WITH_GUESTS_AB]: freeWithGuestsABSteps,
  [eventStepsType.FREE]: freeSteps,
};
